import { useCallback, useEffect, useState } from "react";
import { DeleteTwoTone, EditTwoTone } from "@ant-design/icons";
import {
  Button,
  message,
  Row,
  Col,
  Dropdown,
  Menu,
  Popconfirm,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Typography,
} from "antd";
import ListTable from "../../../common/ListTable";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../CommonComponents/ExtraContent";
import FilterComponent from "../CommonComponents/FilterComponent";
import {
  getData,
  postData,
  deleteData,
  putData,
} from "../../../apiServices/common";
import {
  WAREHOUSE_LIST_ENDPOINT,
  WAREHOUSE_ADD_ENDPOINT,
  WAREHOUSE_EDIT_ENDPOINT,
  WAREHOUSE_DELETE_ENDPOINT,
  GET_CATEGORY_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useSelector } from "react-redux";
import { hasPermission } from "../../../utils/functions";
import {
  WAREHOUSE_CREATE_PERMISSION,
  WAREHOUSE_EDIT_PERMISSION,
  WAREHOUSE_DELETE_PERMISSION,
} from "../../../routes/permissions";
import { useLocation } from "react-router";

const WarehouseConfig = () => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  // States
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [categories, setCategories] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Antd
  const [warehouseForm] = Form.useForm();
  const { Option } = Select;
  const { Title } = Typography;

  // Redux
  const filterData = useSelector(({ filter }) => filter.filterData);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAllWarehouse = useCallback(
    async (filterValues) => {
      const { name } = filterValues || {};
      setLoading(true);
      setFetchError(null);

      const query = `${WAREHOUSE_LIST_ENDPOINT}?page=${currentPage}`;
      const bodyData = {
        ...(name && { name: name }),
      };

      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        setLoading(false);
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data);
      } else {
        setLoading(false);
        setFetchError("Could not load this list.");
        message.error("Error getting warehouse data");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    getAllWarehouse(filterData);
    getAllCategories();
  }, [refresh, filterData, getAllWarehouse]);

  const onModalOpen = () => {
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    warehouseForm.resetFields();
    setIsModalOpen(false);
    setIsEdit(false);
  };

  const refetch = () => {
    setRefresh(refresh + 1);
  };

  const getAllCategories = async () => {
    const response = await getData(GET_CATEGORY_ENDPOINT);
    if (response?.status === 200) {
      setCategories(response?.data?.data?.data);
    } else {
      setLoading(false);
      message.error("Error getting category list");
    }
  };

  const deleteSelectedWarehouse = async (id) => {
    setLoading(true);
    const response = await deleteData(`${WAREHOUSE_DELETE_ENDPOINT}/${id}`);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Warehouse Deleted Successfully");
      refetch();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const editSelectedWarehouse = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status,
      category_id: formValues?.category_id,
    };

    const response = await putData(
      `${WAREHOUSE_EDIT_ENDPOINT}/${formValues?.id}`,
      bodyData,
    );

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Warehouse Edited Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const createNewWarhouse = async (formValues) => {
    setLoading(true);

    const bodyData = {
      name: formValues?.name,
      description: formValues?.description,
      status: formValues?.status,
      category_id: formValues?.category_id,
    };

    const response = await postData(WAREHOUSE_ADD_ENDPOINT, bodyData);

    if (response && response?.code === 200) {
      setLoading(false);
      message.success("Warehouse Created Successfully");
      refetch();
      warehouseForm.resetFields();
      onCloseModal();
    } else {
      setLoading(false);
      message.error("Something went wrong");
    }
  };

  const openModalForEdit = (record) => {
    setIsEdit(true);

    const category_map_info = record?.category_map_info;

    const items_not_in_categories = category_map_info
      .filter((item) => !categories.some((category) => category.id === item.id))
      .map((item) => ({
        id: item?.id,
        name: item?.category_info?.name,
      }));

    const categoriesCopy = [...categories, ...items_not_in_categories];
    setCategories(categoriesCopy);

    warehouseForm.setFieldsValue({
      id: record?.id,
      name: record?.name,
      description: record?.description,
      status: record?.status === "1" ? true : false,
      category_id: record?.category_map_info?.map((item) => item?.id),
    });
    setIsModalOpen(true);
  };

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const tableColumns = [
    {
      title: "Warehouse Name",
      dataIndex: ["name"],
      key: "warehouse_name",
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "warehouse_status",
      render: (_, record) => {
        return <div>{record?.status === "1" ? "Active" : "Inactive"}</div>;
      },
    },
    {
      title: "Description",
      dataIndex: ["description"],
      key: "warehouse_description",
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => {
        return (
          <Dropdown.Button
            overlay={
              <Menu
                items={[
                  {
                    label: (
                      <Popconfirm
                        title="Are you sure you want to delete?"
                        onConfirm={() => {
                          deleteSelectedWarehouse(record?.id);
                        }}
                      >
                        <span>
                          <DeleteTwoTone />
                        </span>
                        <span
                          style={{
                            color: "red",
                            marginLeft: 5,
                          }}
                        >
                          Delete
                        </span>
                      </Popconfirm>
                    ),
                    key: "1",
                    disabled: !hasPermission([WAREHOUSE_DELETE_PERMISSION]),
                  },
                  {
                    label: (
                      <>
                        <span>
                          <EditTwoTone />
                        </span>
                        <span
                          type="button"
                          style={{ marginLeft: 5 }}
                          onClick={() => {
                            openModalForEdit(record);
                          }}
                        >
                          Edit
                        </span>
                      </>
                    ),
                    key: "2",
                    disabled: !hasPermission([WAREHOUSE_EDIT_PERMISSION]),
                  },
                ]}
              />
            }
          ></Dropdown.Button>
        );
      },
    },
  ];

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            extra={
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                handleAddItemOpen={onModalOpen}
                disabled={!hasPermission([WAREHOUSE_CREATE_PERMISSION])}
                addButtonText={
                  hasPermission([WAREHOUSE_CREATE_PERMISSION])
                    ? "Add New"
                    : "No Permission"
                }
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            }
            title={<Title level={5}>Warehouse Configuration</Title>}
          />
        }
        content={
          <>
            <FilterComponent
              tableData={tableData}
              filterAPI={getAllWarehouse}
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />

            <div className="table" style={{ marginTop: 10 }}>
              <ListTable
                loading={loading}
                error={fetchError}
                onRetry={() => getAllWarehouse(filterData)}
                emptyText="No records to show yet."
                dataSource={tableData}
                columns={tableColumns}
                pagination={{
                  showSizeChanger: false,
                  defaultPageSize: metaData?.defaultPageSize,
                  current: metaData?.current,
                  onChange: (newPage) => {
                    changePage(newPage);
                  },
                  defaultCurrent: metaData?.defaultCurrent,
                  total: metaData?.total,
                }}
              />
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        title={isEdit ? "Edit Warehouse" : "Create Warehouse"}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        <Form
          form={warehouseForm}
          layout="vertical"
          onFinish={isEdit ? editSelectedWarehouse : createNewWarhouse}
        >
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item name="id" hidden />
              <Form.Item
                name="name"
                placeholder="Warehouse Name"
                label="Warehouse Name"
                rules={[
                  {
                    required: true,
                    message: "Warehouse Name Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                placeholder="Description"
                label="Description"
                rules={[
                  {
                    required: true,
                    message: "Description Required",
                  },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Form.Item
                name="status"
                placeholder="Status"
                label="Status"
                rules={[
                  {
                    required: true,
                    message: "Status Required",
                  },
                ]}
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category_id"
                placeholder="Category"
                label="Category"
                rules={[
                  {
                    required: true,
                    message: "Category Required",
                  },
                ]}
              >
                <Select
                  placeholder="Select Category"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  showSearch
                  style={{ width: "100%" }}
                  mode="multiple"
                >
                  {categories?.map((item, index) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={6}>
            <Col span={12}>
              <Button
                type="danger"
                style={{ width: "100%" }}
                onClick={() => {
                  onCloseModal();
                }}
              >
                Discard
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "100%" }}
                loading={loading}
              >
                Save
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default WarehouseConfig;
